import * as React from "react";
import MediaQuery from "react-responsive";
import { observer } from "mobx-react";
import { autorun, configure, reaction } from "mobx";

configure({ enforceActions: "never" });

import "./app.scss";

import { AppConfiguration } from "../core/config/app-configuration";
import { ModuleConfiguration } from "../core/config/module-configuration";
import { MapViewerConfiguration } from "../config/map-viewer-configuration";

import { ErrorBoundary } from "../core/components/error-boundary/error-boundary";
import esriConfig from "@arcgis/core/config";

import { MapState } from "../core/map-state";
import { UiState } from "../core/ui-state";
import { LayerStack } from "./map/layer-stack/layer-stack";
import { AppState } from "../stores/app-mobx";

import { Header } from "./header/header";
import { Footer } from "./footer/footer";
import { Sidebar } from "./sidebar/sidebar";
import ProjectSelection from "./map/project_selection";
import { MobileNavigation } from "./navigation/mobile-navigation";
import { ObjektwesenFooter } from "./footer/objektwesen-footer";
import Maintenance from "../core/components/maintenance/maintenance";
import { Icon } from "../core/components/icon/icon";
import { Measurement } from "../core/components/tools/measurement/measurement";
import { ElevationProfile } from "../core/components/tools/elevation-profile/elevation-profile";

import { Button, MessageProps, Ref, Sidebar as SemanticSidebar } from "semantic-ui-react";

import { MessageBox } from "../core/components/message-box/message-box";
import MessageStartup from "../core/components/message-startup/message-startup";
import MessageBlock from "../core/components/message-block/message-block";
import { Popup } from "../core/components/popup/popup";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import CookieBanner from "../core/components/cookie-banner/cookie-banner";
import EsriCompass from "@arcgis/core/widgets/Compass";
import EsriLocate from "@arcgis/core/widgets/Locate";
import { Search } from "../core/components/search/search";
import { MessageConfiguration } from "../core/config/message-configuration";
import * as arcade from "@arcgis/core/arcade";
import { AuthenticationStore } from "../stores/authentication/authentication-store";
import { redirect, Outlet, useLoaderData, ScrollRestoration } from "react-router-dom";
import { BasemapGallery } from "../core/components/basemap-gallery/basemap-gallery";
import { GeoProxy } from "../core/geo-proxy";
import { TopicConfiguration } from "../config/topic-configuration";
import { RootStore } from "../stores/root-store";
import { updateUninitializedCreationStatus } from "../stores/area/creation-area-update";

/* Lazy load Webmap to show loader while importing */
const WebMap = React.lazy(() =>
    import("../core/geo").then((module) => ({ default: module.WebMap }))
);

export interface AppProps {
    config: AppConfiguration;
    moduleConfig: ModuleConfiguration;
    appStore: AppState;
    authenticationStore: AuthenticationStore;
    uiStore: UiState;
    rootStore: RootStore;
}

/**
 * Anwendungsklasse
 */
@observer
export class App extends React.Component<AppProps, any> {
    autorunDisposers: any[] = [];
    appStore: AppState;
    authenticationStore: AuthenticationStore;
    uiStore: UiState;
    mapStore: MapState;
    rootStore: RootStore;
    mapViewerConfig: MapViewerConfiguration;
    appConfig: AppConfiguration;

    appUiRef = React.createRef<HTMLDivElement>();

    defaultScale?: number;

    constructor(props: AppProps) {
        super(props);
        this.appStore = this.props.appStore;
        this.uiStore = this.props.uiStore;
        this.mapStore = new MapState();
        this.rootStore = this.props.rootStore;
        this.authenticationStore = this.props.authenticationStore;
        this.state = {
            northFacing: true,
            alertMessage: [],
            startupMessage: [],
            addClass: []
        };

        this.uiStore.setUiSize(window.innerWidth, window.innerHeight);

        this.mapViewerConfig = this.props.moduleConfig as MapViewerConfiguration;
        this.appConfig = props.config as AppConfiguration;
        this.defaultScale = this.appConfig.mapView.scale;

        this.appStore.setTopic({
            id: "fff",
            name: this.mapViewerConfig.appName,
            label: this.mapViewerConfig.appName,
            routes: ["/"]
        } as TopicConfiguration);

        // configure proxy page
        const geoProxy = new GeoProxy(this.appConfig.proxy);
        geoProxy.loadRules();
        esriConfig.request.proxyUrl = this.appConfig.proxy.url;

        // configure assets path
        if (this.appConfig.assetsPath) {
            esriConfig.assetsPath = this.appConfig.assetsPath;
        }
        // configure fonts URL
        if (this.appConfig.fontsUrl) {
            esriConfig.fontsUrl = this.appConfig.fontsUrl;
        }
        // configure GeometryService URL
        if (this.appConfig.geometryServiceUrl) {
            esriConfig.geometryServiceUrl = this.appConfig.geometryServiceUrl;
        }
        // configure portal
        if (this.appConfig.portalUrl) {
            esriConfig.portalUrl = this.appConfig.portalUrl;
            // Esri Anmeldedialog unterdrücken
            // eslint-disable-next-line max-len
            // https://community.esri.com/t5/arcgis-api-for-javascript-questions/detect-or-prevent-the-arcgis-authentication-dialog/td-p/1047565
            IdentityManager.on("dialog-create", () => {
                // eslint-disable-next-line no-console
                console.warn("Ungenügende Berechtigung (IdentityManager dialog-create)");
                IdentityManager.dialog.destroy();
            });
        } else {
            // eslint-disable-next-line no-console
            console.warn("No portal connection defined.");
        }
        this.mapStore.setAppConfig(this.appConfig);
        this.handleMapLoad = this.handleMapLoad.bind(this);
        this.handleResize = this.handleResize.bind(this);

        // Zufälliger Extent aus Bookmarks, falls weder center noch extent definiert wurde
        if (!this.appConfig.mapView.center && !this.appConfig.mapView.extent
            && !this.appConfig.mapView.viewpoint) {
            const { bookmarks } = this.mapViewerConfig;
            const bookmark = bookmarks[Math.floor(Math.random() * bookmarks.length)];
            if (bookmark?.viewpoint) {
                this.appStore.startupExtent = bookmark;
                delete this.appConfig.mapView.center;
                delete this.appConfig.mapView.scale;
                delete this.appConfig.mapView.zoom;
                this.appConfig.mapView.viewpoint = bookmark.viewpoint;
                if (this.appConfig.mapView.scale) {
                    this.appConfig.mapView.viewpoint.scale = this.appConfig.mapView.scale;
                }
            }
        }
    }

    handleResize(): void {
        this.checkUiSize();
    }

    checkUiSize(): void {
        const uiHeight = this.appUiRef.current?.clientHeight;
        const uiWidth = this.appUiRef.current?.clientWidth;
        const isSmallDevice = (window.innerWidth <= this.mapViewerConfig.breakpoints.md);
        this.uiStore.setUiSize(uiWidth, uiHeight, isSmallDevice);
    }

    componentDidMount(): void {
        document.title = this.mapViewerConfig.appName;

        this.mapStore.setWebMap(this.mapViewerConfig.webMap);

        // rerun until navigate is available
        autorun((react: any) => {
            this.checkUiSize();
            react.dispose(); // stop autorun
        });
        autorun((react: any) => {
            const search = this.uiStore.widgets.get("search") as Search;
            if (!search || !search.state.viewModel) {
                return;
            }
            this.checkUiSize();
            this.prepareSearch(search);
            react.dispose(); // stop autorun
        });
        this.checkUiSize();
        window.addEventListener("resize", this.handleResize);
        this.updateMessages();
        this.autorunDisposers.push(reaction(() => this.mapStore.activeLayers, () => {
            this.updateMessages();
        }));
        autorun(() => {
            const search = this.uiStore.widgets.get("search-2") as Search;
            if (!search || !search.state.viewModel) {
                return;
            }
            // reaction.dispose(); // stop autorun
            this.prepareSearch(search);
        });

        updateUninitializedCreationStatus(this.rootStore.session);
    }

    prepareSearch(search: Search): void {
        if (search.state.viewModel.hasEventListener("suggestionSelected")) {
            return;
        }
        search.state.viewModel.on("suggestionSelected", () => {
            this.appStore.setShowMap(true);
        });
    }

    componentWillUnmount(): void {
        // dispose reactions
        window.removeEventListener("resize", this.handleResize);
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
    }

    setSidebarVisible(visible: boolean): void {
        this.uiStore.setSidebarVisible(visible);
    }

    handleToggleMap(e: React.MouseEvent): void {
        const { showMap } = this.appStore;
        this.appStore.setShowMap(!showMap);
        this.appStore.setUserShowMap(!showMap);
        e.preventDefault();
        // Wenn blur direkt via e.target aufgerufen wird, reklamiert Webstorm:
        // TS2339: Property 'blur' does not exist on type 'EventTarget'.
        const target = e.target as HTMLElement;
        target.blur();
    }

    render(): JSX.Element | null {
        let mapProperties: __esri.WebMapProperties | undefined;
        const messages: JSX.Element[] = [];
        const { maintenance } = this.appConfig;
        const { sidebarVisible } = this.uiStore;
        const { status } = this.mapStore;
        const { showMap } = this.appStore;
        const { northFacing } = this.state;
        if (this.mapStore.webMap) {
            mapProperties = this.mapStore.webMap as __esri.WebMapProperties;
            if (this.mapViewerConfig.meta && Array.isArray(this.mapViewerConfig.meta.alerts)) {
                this.mapViewerConfig.meta.alerts.forEach((message, index) => {
                    // quick and dirty!
                    const alertContent = <p dangerouslySetInnerHTML={{ __html: message.content }}/>;
                    messages.push(
                        <MessageBlock
                            key={index}
                            index={index}
                            topic={"objekt.lu"}
                            header={message.title}
                            className={message.class ? message.class : undefined}
                            content={alertContent}
                            appConfig={this.appConfig}/>
                    );
                });
            }
        }
        let addClass = "geo-app-body";
        addClass += (!showMap ? " hide" : "");
        addClass += (this.appStore.topic) ? " topic-" + this.appStore.topic.id : "";
        addClass += (this.appStore.subtopic) ? " subtopic-" + this.appStore.subtopic.id : "";
        addClass += " " + status;
        addClass += (northFacing ? " geo-north-facing" : " geo-not-north-facing");
        const mapContent: JSX.Element = (
            <div id="map-root">
                <ScrollRestoration/>
                <div id="map-toggler">
                    <Button onClick={(e: React.MouseEvent) => this.handleToggleMap(e)}>
                        {/* eslint-disable-next-line max-len */}
                        <Icon
                            name={showMap ? "navigate_up" : "navigate_down"}/> {showMap ? "Karte ausblenden" : "Karte einblenden"}
                    </Button>
                </div>
                <div className={addClass}>
                    <React.Suspense fallback={
                        <div className="pre-loader">
                            <div className="pre-loader-inner">Anwendung wird geladen ...
                            </div>
                        </div>
                    }>
                        <div className="pre-loader">
                            <div className="pre-loader-inner">Anwendung wird geladen ...</div>
                        </div>
                        <WebMap
                            mapProperties={mapProperties}
                            viewProperties={
                                this.appConfig.mapView as __esri.MapViewProperties}
                            onLoad={this.handleMapLoad}
                            store={this.mapStore}
                            uiStore={this.uiStore}>
                            <LayerStack
                                mapStore={this.mapStore}
                                appStore={this.appStore}
                                uiStore={this.uiStore}
                                config={this.mapViewerConfig.layers}
                            />
                        </WebMap>
                    </React.Suspense>
                    <Ref innerRef={this.appUiRef}>
                        <div className={"geo-app"}>
                            <Maintenance config={maintenance}/>
                            <div className={"geo-ui-root"}>
                                <div className={"geo-ui"}>
                                    <div className={
                                        "geo-ui-inner-container geo-ui-manual-container"}>
                                        <Popup config={this.mapViewerConfig.popup}
                                               mapViewerConfig={this.mapViewerConfig}
                                               appConfig={this.appConfig}
                                               view={this.mapStore.view}
                                               uiStore={this.uiStore}
                                               appStore={this.appStore}
                                               mapStore={this.mapStore}/>
                                    </div>
                                    <div className={"geo-ui-inner-container geo-ui-corner-container"}>
                                        <MediaQuery minWidth={(this.mapViewerConfig.breakpoints.md + 1)}>
                                            <Sidebar
                                                appConfig={this.appConfig}
                                                appStore={this.appStore}
                                                mapStore={this.mapStore}
                                                uiStore={this.uiStore}
                                                config={this.mapViewerConfig.sidebar}
                                                mapViewerConfig={this.mapViewerConfig}/>
                                        </MediaQuery>
                                        <div className={"geo-ui-top-left geo-ui-corner"}>
                                            <ElevationProfile
                                                appStore={this.appStore}
                                                config={this.mapViewerConfig.tools?.profile}
                                                uiStore={this.uiStore}
                                                view={this.mapStore.view}/>
                                            <Measurement
                                                appStore={this.appStore}
                                                uiStore={this.uiStore}
                                                view={this.mapStore.view}/>
                                        </div>
                                        <div className={"geo-ui-top-right geo-ui-corner"}/>
                                        <div className={"geo-ui-bottom-right geo-ui-corner"}>
                                            <BasemapGallery config={this.appConfig.basemapGallery}
                                                            view={this.mapStore.view}
                                                            uiStore={this.uiStore}
                                                            mapStore={this.mapStore}/>
                                        </div>
                                        <div className={"geo-ui-bottom-left geo-ui-corner"}>
                                            {messages.length > 0 && <div className="geo-messages">{messages}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Footer appStore={this.appStore}
                                    appConfig={this.appConfig}
                                    uiStore={this.uiStore}
                                    mapStore={this.mapStore}
                                    mapViewerConfig={this.mapViewerConfig}/>
                        </div>
                    </Ref>
                    <MessageBox store={this.appStore}/>
                    <ProjectSelection
                        view={this.mapStore.view}
                        uiStore={this.uiStore}
                        appStore={this.appStore}/>
                </div>
            </div>
        );
        const { menuVisible, isSmallDevice, topicswitcherVisible, uiHeight } = this.uiStore;
        const { basemapOpacity } = this.mapStore;
        let sidebarClass = "geo-menu-" + ((menuVisible && !topicswitcherVisible) ? "visible" : "hidden");
        sidebarClass += (this.uiStore.sidebarHigh) ? " geo-sidebar-is_high" : "";
        sidebarClass += " " + this.state.addClass.join(" ");
        sidebarClass += isSmallDevice ? " geo-small_device" : "";
        sidebarClass += (basemapOpacity <= 5) ? " show-grid" : "";
        const cssProperties = { "--uiHeight": uiHeight + "px" };
        return (
            <ErrorBoundary>
                <SemanticSidebar.Pushable className={sidebarClass} style={cssProperties}>
                    <SemanticSidebar
                        animation="push"
                        onHide={() => this.setSidebarVisible(false)}
                        visible={sidebarVisible}>
                        <MobileNavigation
                            config={this.mapViewerConfig.navigation}
                            uiStore={this.uiStore}/>
                    </SemanticSidebar>

                    <SemanticSidebar.Pusher>
                        <div className="page">
                            <Header appConfig={this.appConfig}
                                    uiStore={this.uiStore}
                                    mapStore={this.mapStore}
                                    capabilities={["search"]}
                                    searchConfig={this.appConfig.search}
                                    mapViewerConfig={this.mapViewerConfig}
                                    authenticationStore={this.authenticationStore}
                            />
                            <div id="layout" className="wrapper container layout">

                                <main className="layout__main">
                                    <div className="content one-col">
                                        {mapContent}
                                        <div className="content__main">
                                            <Outlet/>
                                        </div>
                                    </div>
                                </main>
                            </div>
                            <ObjektwesenFooter/>
                        </div>
                    </SemanticSidebar.Pusher>
                </SemanticSidebar.Pushable>
                <CookieBanner mapViewerConfig={this.mapViewerConfig}/>
            </ErrorBoundary>
        );
    }

    handleMapLoad(map: __esri.Map, view: __esri.MapView | __esri.SceneView): void {
        // TODO: AppState status erst auf complete schalten, wenn alle Widgets geladen sind
        // this.appStore.setState({ status: "complete" });
        if (this.mapViewerConfig.locateButtonEnabled) {
            const locateWidget = new EsriLocate({
                view
            });
            view.ui.add(locateWidget, "top-right");
        }
        // mode zoom buttons to top right
        view.ui.move("zoom", "top-right");
        const compassWidget = new EsriCompass({
            view
        });
        view.ui.add(compassWidget, "top-right");
        view.watch("rotation", (newValue: number) => {
            this.setState({ northFacing: newValue === 0 });
        });

        view.closePopup();
    }

    updateMessages() {
        const startupMessage = this.prepareMessagesContent(
            this.mapViewerConfig.meta?.startupMessage ?
                [this.mapViewerConfig.meta.startupMessage] : undefined, true);
        Promise.all(startupMessage).then((msgs) => {
            this.setState({ startupMessage: msgs.filter((m) => m) });
        });
        const alertMessages = this.prepareMessagesContent(this.mapViewerConfig.meta.alerts);
        Promise.all(alertMessages).then((msgs) => {
            this.setState({ alertMessages: msgs.filter((m) => m) });
        });
    }

    // eslint-disable-next-line max-len
    prepareMessagesContent(messagesConfig?: MessageConfiguration[], startup = false): Array<Promise<JSX.Element | undefined>> {
        const messagePromises: Array<Promise<JSX.Element | undefined>> = [];
        if (messagesConfig && Array.isArray(messagesConfig)) {
            const profile: __esri.Profile = {
                variables: [{
                    name: "$subtopicId",
                    type: "text"
                }, {
                    name: "$activeLayers",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }]
            };
            messagesConfig.forEach((message, index) => {
                if (typeof message.condition === "undefined") {
                    messagePromises.push(new Promise((resolve) => {
                        resolve(this.prepareMessageContent(message, index, startup));
                    }));
                } else {
                    if (this.mapStore.activeLayers) {
                        const arcadeExecutor = arcade.createArcadeExecutor(message.condition, profile);
                        messagePromises.push(new Promise((resolve) => {
                            arcadeExecutor.then((ae) => {
                                const condition = ae.execute({
                                    $activeLayers: this.mapStore.activeLayers,
                                    $subtopicId: (this.appStore.subtopicId ? this.appStore.subtopicId : "")
                                });
                                let m: JSX.Element | undefined;
                                if (condition) {
                                    m = this.prepareMessageContent(message, index, startup);
                                }
                                resolve(m);
                            });
                        }));
                    }
                }
            });
        }
        return messagePromises;
    }

    prepareMessageContent(messageConfig: MessageConfiguration, index: number, startup = false): JSX.Element {
        let message: JSX.Element;
        if (startup) {
            message = <MessageStartup
                key={index}
                topic={this.appStore.topic.id}
                header={messageConfig.title}
                className={messageConfig.class ? messageConfig.class : ""}
                content={messageConfig.content}
                appConfig={this.appConfig}/>;
        } else {
            message = <MessageBlock
                key={index}
                index={index}
                topic={this.appStore.topic.id}
                header={messageConfig.title}
                className={messageConfig.class ? (messageConfig as MessageProps).class : undefined}
                content={messageConfig.content}
                appConfig={this.appConfig}/>;
        }
        return message;
    }
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function withParams(Component: any): (props: AppProps) => JSX.Element {
    // eslint-disable-next-line react/display-name
    return (props) => <Component {...props} userStore={useLoaderData() as AuthenticationStore}/>;
}

/**
 * Return the App within a function to use hocks. The hock is needed to get the user store.
 */
export default withParams(App);

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export const loader = async (authenticationStore: AuthenticationStore) => {
    if (!authenticationStore.loggedIn()) {
        try {
            await authenticationStore.authenticate();
        } catch (error) {
            await authenticationStore.logout();
            return redirect("/anmelden");
        }

        if (!authenticationStore.authorized()) {
            await authenticationStore.logout();
            return redirect("/anmelden");
        }
    }

    return authenticationStore;
}
